import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  TextField,
} from '@material-ui/core';
import { RemoveRedEye } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import { StockMovement, StockMovementOperation, StockMovementType } from 'types/models/StockMovement';
import { formatDateTime } from 'utils/datetime';
import { getStockMovements } from 'services/stockMovementsServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import SelectField from 'components/SelectField';
import { parseDateToFormik, parseDateTimeLumen } from 'utils/datetime';
import { mapToSelectOptions } from 'utils/select';
import Venue from 'types/models/Venue';
import UserRoleType from 'types/enums/UserRoleType';
import useSetupInitialState from 'hooks/useSetupInitialState';

function getFinishedItem(movement: StockMovement) {
  return movement.items?.find((item) => item.operation === StockMovementOperation.INCREASE);
}

interface ProductionFilters {
  dateFrom?: string;
  dateTo?: string;
}

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedMovement } = React.useContext(EditStockModuleContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [venueId, setVenueId] = React.useState<string>('');
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockMovement>>();
  const [filters, setFilters] = React.useState<ProductionFilters | null>(null);

  const [defaultRange] = React.useState(() => ({
    from: moment().subtract(7, 'days').toDate(),
    to: moment().add(1, 'day').endOf('day').toDate(),
  }));

  const formInitialValues = {
    dateFrom: parseDateToFormik(defaultRange.from, ''),
    dateTo: parseDateToFormik(defaultRange.to, ''),
  };

  const [dateFrom, setDateFrom] = React.useState<string>(formInitialValues.dateFrom);
  const [dateTo, setDateTo] = React.useState<string>(formInitialValues.dateTo);

  const buildFilters = (values: { dateFrom: string; dateTo: string }): ProductionFilters => ({
    dateFrom: values.dateFrom ? parseDateTimeLumen(moment(values.dateFrom).toDate(), '') : undefined,
    dateTo: values.dateTo ? parseDateTimeLumen(moment(values.dateTo).toDate(), '') : undefined,
  });

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      if (isVenueEditor) {
        return;
      }
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    setVenueId(entity.id);
  }, [isVenueEditor, entity]);

  React.useEffect(() => {
    setCurrentPageListNumber(1);
    setFilters(buildFilters({ dateFrom, dateTo }));
  }, [dateFrom, dateTo]);

  const loadData = React.useCallback(
    async (page?: number) => {
      if (!filters) return;
      try {
        const paginationData = await getStockMovements(page, undefined, {
          type: StockMovementType.PRODUCTION,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          venueId,
        });
        if (paginationData) setPaginationData(paginationData);
      } catch (error) {
        console.log(error);
      }
    },
    [filters, venueId],
  );

  React.useEffect(() => {
    if (!venueId) {
      setPaginationData(undefined);
      return;
    }
    if (filters) {
      loadData(currentPageListNumber ?? 1);
    }
  }, [currentPageListNumber, location.search, loadData, venueId, filters]);

  const renderContent = (paginationData: StockMovement[]): JSX.Element => {
    return (
      <>
        {paginationData.map((movement) => {
          const finishedItem = getFinishedItem(movement);

          return (
            <TableRow key={movement.id}>
              <TableCell>{formatDateTime(movement.localDatetime)}</TableCell>
              <TableCell>{finishedItem?.globalProduct?.name ?? '—'}</TableCell>
              <TableCell>{finishedItem?.quantity ?? '—'}</TableCell>
              <TableCell>{movement.source?.name ?? '—'}</TableCell>
              <TableCell>{movement.createdBy?.name ?? '—'}</TableCell>
              <TableCell>{renderViewButton(movement)}</TableCell>
            </TableRow>
          );
        })}
      </>
    );
  };

  const renderViewButton = (movement: StockMovement): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!movement) return;
          setSelectedMovement(movement);
          history.push(`/private/stock/movements/${movement.id}/view?type=${StockMovementType.PRODUCTION}`);
        }}
      >
        <RemoveRedEye />
      </IconButton>
    );
  };

  return (
    <>
      {!isVenueEditor && (
        <SelectField
          name="venueId"
          label={t('stock.receipts.filters.venue')}
          placeholder={t('stock.receipts.filters.venue-placeholder')}
          value={venueId}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => setVenueId(value as string)}
          touched={false}
          error={false}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={() => undefined}
          isDisabled={false}
        />
      )}

      <div style={{ display: 'flex', gap: '1rem' }}>
        <TextField
          margin="dense"
          fullWidth
          name="dateFrom"
          label={t('stock.productions.filters.dateFrom')}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          margin="dense"
          fullWidth
          name="dateTo"
          label={t('stock.productions.filters.dateTo')}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
        />
      </div>

      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('/private/stock/productions/create');
        }}
      >
        {t('stock.productions.create')}
      </Button>

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.productions.table.date')}</TableCell>
              <TableCell>{t('stock.productions.table.product')}</TableCell>
              <TableCell>{t('stock.productions.table.quantity')}</TableCell>
              <TableCell>{t('stock.productions.table.center')}</TableCell>
              <TableCell>{t('stock.productions.table.user')}</TableCell>
              <TableCell>{t('stock.productions.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.productions.table.no-results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
